import ActionPlanSummary from '@/components/action-plan-summary/action-plan-summary';
import AboveAverageBadge from '@/components/badges/above-average-badge/above-average-badge';
import AverageBadge from '@/components/badges/average-badge/average-badge';
import BelowAverageBadge from '@/components/badges/below-average-badge/below-average-badge';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import DashboardNavigationBar from '@/components/dashboard/navigation-bar/dashboard-navigation-bar';
import MainContent from '@/components/main-layout/components/main-content/main-content';
import ModalDetailRiskFactor from '@/components/modal-detail-risk-factor/modal-detail-risk-factor';
import Resources from '@/components/resources/resources';
import SubNavBar from '@/components/subnav-bar/subnav-bar';
import { RISK_LOWER_THRESHOLD, RISK_UPPER_THRESHOLD } from '@/constants/risks';
import useIsMobile from '@/hooks/use-is-mobile';
import { Box, Divider, Flex } from '@cmpsr/components';
import { useNavigate } from 'react-router-dom';
import ModalInterventionDetails from '../../action-plan/components/modals/details/modal-intervention-details';
import HeaderRisks from '../common/header-risks/header-risks';
import RisksSectionTitle from '../common/risks-section-title/risks-section-title';
import RiskComparison from './components/risk-comparison/risk-comparison';
import SingleCancerDescription from './components/single-cancer-description/single-cancer-description';
import SingleCancerGraph from './components/single-cancer-graph/single-cancer-graph';
import { SingleCancerSummary } from './components/single-cancer-summary/single-cancer-summary';
import useSingleCancerViewController from './single-cancer-view.controller';
import TestingPlanCard from '../../testing-plan/components/your-testing-plan/components/testing-plan-card/testing-plan-card';

export default function SingleCancerViewPage() {
  const {
    cancerId,
    icon,
    cancerName,
    currentRisk,
    averageRisk,
    about,
    potentialRisk,
    decadeRisk,
    averageAgeOnset,
    fiveYearSurvivalRate,
    showModalDetailRiskFactor,
    onHideModalDetailRiskFactor,
    selectedRiskFactor,
    affectedCancerTypes,
    impactsByCancerType,
    recommendedInterventions,
    handleOpenDetailsModal,
    selectedIntervention,
    isDetailsModalOpen,
    onCloseDetailsModal,
    testingPlanItem,
    cancerGuideUrl,
    riskComparisonData,
    riskBadgeColor,
    riskAnalysisData,
  } = useSingleCancerViewController();

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const riskBadge =
    currentRisk > averageRisk * RISK_UPPER_THRESHOLD ? (
      <AboveAverageBadge isRisk />
    ) : currentRisk < averageRisk * RISK_LOWER_THRESHOLD ? (
      <BelowAverageBadge isRisk />
    ) : (
      <AverageBadge isRisk />
    );

  return (
    <>
      <DashboardNavigationBar />
      <MainContent>
        <SubNavBar onClick={() => navigate(-1)} px={{ base: '5', md: '8', lg: '16', xl: '24', xxl: '32' }}>
          Back
        </SubNavBar>
        <DashboardLayout containerProps={{ pt: 0 }}>
          <DashboardGrid>
            <Flex as="main" direction="column" gap={8} className="dashboard-reset">
              <HeaderRisks label={cancerName} icon={icon}>
                {riskBadge}
              </HeaderRisks>

              <SingleCancerGraph
                currentRiskColor={riskBadgeColor}
                riskData={riskAnalysisData}
                cancerName={cancerName}
              />
              <Divider />

              <RisksSectionTitle headline="Risk Summary" />
              <SingleCancerSummary
                currentRisk={currentRisk}
                potentialRisk={potentialRisk}
                averageRisk={averageRisk}
                decadeRisk={decadeRisk}
                averageAgeOnset={averageAgeOnset}
                fiveYearSurvivalRate={fiveYearSurvivalRate}
                cancerType={cancerName}
              />
              <Divider />

              <RisksSectionTitle headline={`Factors driving your ${cancerName} risk`} />
              <RiskComparison {...riskComparisonData} />
              <Divider />

              {recommendedInterventions && recommendedInterventions.length ? (
                <>
                  <RisksSectionTitle headline="Your Recommended Actions" />
                  <ActionPlanSummary
                    recommendedInterventions={recommendedInterventions}
                    handleOpenDetailsModal={handleOpenDetailsModal}
                    cancerId={cancerId}
                  />
                  <Divider />
                </>
              ) : null}

              {testingPlanItem && (
                <>
                  <RisksSectionTitle headline="Your Recommended Test" />
                  <TestingPlanCard screeningTypeData={testingPlanItem} hideCompleteTest />
                  <Divider />
                </>
              )}

              <RisksSectionTitle headline={'About '} subheadline={cancerName} />
              <SingleCancerDescription about={about} cancerGuideUrl={cancerGuideUrl} />

              {selectedRiskFactor && (
                <ModalDetailRiskFactor
                  isOpen={showModalDetailRiskFactor}
                  onClose={onHideModalDetailRiskFactor}
                  riskFactor={selectedRiskFactor}
                  affectedCancerTypes={affectedCancerTypes}
                  impactsByCancerType={impactsByCancerType}
                />
              )}
              {selectedIntervention && (
                <ModalInterventionDetails
                  isOpen={isDetailsModalOpen}
                  onClose={onCloseDetailsModal}
                  intervention={selectedIntervention}
                  navToActionPlanHome={true}
                />
              )}
            </Flex>
            <Box as="aside">
              {isMobile && <Divider my="spacer-8" />}
              <Resources width="full" />
            </Box>
          </DashboardGrid>
        </DashboardLayout>
      </MainContent>
    </>
  );
}
